import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RequireAuth } from './auth/RequireAuth.tsx';
import { ToastProvider } from './components/Toast.tsx';
import { SessionProvider } from './hooks/useSession.tsx';
import { Shell } from './layout/Shell.tsx';
import { AccountDetail } from './pages/AccountDetail.tsx';
import { AccountsList } from './pages/AccountsList.tsx';
import { Audit } from './pages/Audit.tsx';
import { ChannelsPage } from './pages/ChannelsPage.tsx';
import { ChannelDetail } from './pages/ChannelDetail.tsx';
import { CronRunsPage } from './pages/CronRunsPage.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { DrCheckPage } from './pages/DrCheckPage.tsx';
import { FailedDeductions } from './pages/FailedDeductions.tsx';
import { BillingAnomalies } from './pages/BillingAnomalies.tsx';
import { GiftCards } from './pages/GiftCards.tsx';
import { Login } from './pages/Login.tsx';
import { ModelDetail } from './pages/ModelDetail.tsx';
import { ModelsList } from './pages/ModelsList.tsx';
import { NewUpstreamWizard } from './pages/NewUpstreamWizard.tsx';
import { OpsUsersPage } from './pages/OpsUsersPage.tsx';
import { OpsRequestsPage } from './pages/OpsRequestsPage.tsx';
import { OpsUsagePage } from './pages/OpsUsagePage.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { PaymentProvidersPage } from './pages/PaymentProvidersPage.tsx';
import { PaymentsOrdersPage } from './pages/PaymentsOrdersPage.tsx';
import { PricingPage } from './pages/PricingPage.tsx';
import { ReportsPage } from './pages/ReportsPage.tsx';
import { RevenuePage } from './pages/RevenuePage.tsx';
import { UpstreamDetail } from './pages/UpstreamDetail.tsx';
import { UpstreamsList } from './pages/UpstreamsList.tsx';
import { VendorsPage } from './pages/VendorsPage.tsx';
import { WebhookEventsPage } from './pages/WebhookEventsPage.tsx';

/**
 * Top-level route table.
 *
 *   /login                  — unauthenticated
 *   /* (anything else)      — gated by RequireAuth, rendered inside Shell
 *
 * The current canonical IA lives under `/overview`, `/customers`,
 * `/finance`, `/supply`, `/governance`, and `/system`. Legacy flat
 * routes stay as redirect-only compatibility shims.
 * The QueryClientProvider lives in `main.tsx` so tests can mount a fresh
 * client per render.
 */
export function App(): JSX.Element {
  return (
    <SessionProvider>
      <ToastProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Shell>
                  <Routes>
                    <Route path="/" element={<RedirectTo to="/overview" />} />
                    <Route path="/overview" element={<OpsUsagePage variant="overview" />} />
                    <Route path="/overview/route-health" element={<Dashboard />} />
                    <Route path="/customers/accounts" element={<AccountsList />} />
                    <Route path="/customers/accounts/:accountId" element={<AccountDetail />} />
                    <Route path="/customers/usage" element={<OpsUsagePage />} />
                    <Route path="/customers/requests" element={<OpsRequestsPage />} />
                    <Route path="/customers/failed-deductions" element={<FailedDeductions />} />
                    <Route path="/customers/billing-anomalies" element={<BillingAnomalies />} />
                    <Route path="/finance/revenue" element={<RevenuePage />} />
                    <Route path="/finance/payment-orders" element={<PaymentsOrdersPage />} />
                    <Route path="/finance/payment-providers" element={<PaymentProvidersPage />} />
                    <Route path="/finance/gift-cards" element={<GiftCards />} />
                    <Route path="/finance/fx-policy" element={<PricingPage />} />
                    <Route path="/finance/reports" element={<ReportsPage />} />
                    <Route path="/supply/upstreams" element={<UpstreamsList />} />
                    <Route path="/supply/upstreams/new" element={<NewUpstreamWizard />} />
                    <Route path="/supply/upstreams/:id" element={<UpstreamDetail />} />
                    <Route path="/supply/models" element={<ModelsList />} />
                    <Route path="/supply/models/:vendor/:model" element={<ModelDetail />} />
                    <Route path="/supply/vendors" element={<VendorsPage />} />
                    <Route path="/supply/channels" element={<ChannelsPage />} />
                    <Route path="/supply/channels/:id" element={<ChannelDetail />} />
                    <Route path="/governance/audit" element={<Audit />} />
                    <Route path="/governance/ops-users" element={<OpsUsersPage />} />
                    <Route path="/system/webhook-events" element={<WebhookEventsPage />} />
                    <Route path="/system/cron-runs" element={<CronRunsPage />} />
                    <Route path="/system/dr-check" element={<DrCheckPage />} />
                    <Route path="/upstreams" element={<RedirectTo to="/supply/upstreams" />} />
                    <Route path="/upstreams/new" element={<RedirectTo to="/supply/upstreams/new" />} />
                    <Route path="/upstreams/:id" element={<RedirectToPrefix from="/upstreams" to="/supply/upstreams" />} />
                    <Route path="/models" element={<RedirectTo to="/supply/models" />} />
                    <Route path="/models/:vendor/:model" element={<RedirectToPrefix from="/models" to="/supply/models" />} />
                    <Route path="/gift-cards" element={<RedirectTo to="/finance/gift-cards" />} />
                    <Route path="/accounts" element={<RedirectTo to="/customers/accounts" />} />
                    <Route path="/accounts/:accountId" element={<RedirectToPrefix from="/accounts" to="/customers/accounts" />} />
                    <Route path="/failed-deductions" element={<RedirectTo to="/customers/failed-deductions" />} />
                    <Route path="/billing-anomalies" element={<RedirectTo to="/customers/billing-anomalies" />} />
                    <Route path="/payments/orders" element={<RedirectTo to="/finance/payment-orders" />} />
                    <Route path="/payment-providers" element={<RedirectTo to="/finance/payment-providers" />} />
                    <Route path="/pricing" element={<RedirectTo to="/finance/fx-policy" />} />
                    <Route path="/channels" element={<RedirectTo to="/supply/channels" />} />
                    <Route path="/channels/:id" element={<RedirectToPrefix from="/channels" to="/supply/channels" />} />
                    <Route path="/vendors" element={<RedirectTo to="/supply/vendors" />} />
                    <Route path="/revenue" element={<RedirectTo to="/finance/revenue" />} />
                    <Route path="/reports" element={<RedirectTo to="/finance/reports" />} />
                    <Route path="/webhook-events" element={<RedirectTo to="/system/webhook-events" />} />
                    <Route path="/cron-runs" element={<RedirectTo to="/system/cron-runs" />} />
                    <Route path="/ops-users" element={<RedirectTo to="/governance/ops-users" />} />
                    <Route path="/dr-check" element={<RedirectTo to="/system/dr-check" />} />
                    <Route path="/support/requests" element={<RedirectTo to="/customers/requests" />} />
                    <Route path="/audit" element={<RedirectTo to="/governance/audit" />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Shell>
              </RequireAuth>
            }
          />
        </Routes>
      </ToastProvider>
    </SessionProvider>
  );
}

function RedirectTo({ to }: { readonly to: string }): JSX.Element {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

function RedirectToPrefix({ from, to }: { readonly from: string; readonly to: string }): JSX.Element {
  const location = useLocation();
  const suffix = location.pathname.startsWith(from) ? location.pathname.slice(from.length) : '';
  return <Navigate to={{ pathname: `${to}${suffix}`, search: location.search }} replace />;
}
